// HU-47 (soporte de HU-24) · EL CATÁLOGO DE TIPOS DE AVISO, en un solo sitio (14-sep-2026).
//
// El desplegable «Qué avisos recibe» ofrecía cuatro tipos y el backend ya emite siete:
// quien quisiera recibir el escalamiento por plazo vencido o el aviso del informe final
// no tenía forma de darse de alta, y la lista de destinatarios mostraba el código crudo
// (`informe_final_por_vencer`) para los que no conocía. Aquí viven TODOS los que el
// servidor emite hoy, con su etiqueta en español, y de aquí leen el desplegable y la
// tabla: una sola lista, una sola forma de nombrarlos.
//
// ⚠ La lista sigue ABIERTA por diseño: `destinatario_aviso.tipo_aviso` es `text` sin
// CHECK, y el dominio lo dice explícito —un enum obligaría a redesplegar para añadir un
// tipo, que es justo lo que HU-47 promete evitar—. Esto son las opciones que la pantalla
// OFRECE, no un dominio cerrado; un código que no esté aquí se muestra tal cual, que es
// información y no un hueco. `caso_registrado` es el único que el dominio conoce por
// nombre, porque es el que dispara HU-10.
//
// De dónde sale cada uno (backend):
//   · caso_registrado            notificaciones · avisar_caso_registrado (HU-10)
//   · plazo_por_vencer           regulatorio · alertas_de_plazo (HU-24-C1)
//   · escalamiento_vencido       regulatorio · alertas_de_plazo (HU-24-C2, al Gerente General)
//   · informe_final_por_vencer   regulatorio · alertas_de_plazo (HU-24-C10)
//   · informe_final_escalamiento regulatorio · alertas_de_plazo (HU-24-C10)
//   · cargo_pendiente            regulatorio · alertas_de_plazo (HU-24-C12, acuse demorado)
//   · especialista_invitado      notificaciones · avisar_especialista_invitado (HU-43)
//   · caso_cerrado               previsto; se conserva porque ya estaba ofrecido.

export type TipoDeAviso = {
  /** El código tal como viaja en `tipo_aviso`. */
  codigo: string;
  /** La etiqueta que lee la persona. */
  nombre: string;
  /** La ayuda del desplegable: a quién se manda y por qué HU. */
  nota?: string;
};

export const TIPOS_DE_AVISO: readonly TipoDeAviso[] = [
  { codigo: 'caso_registrado', nombre: 'Caso registrado', nota: 'Avisa al Comité, Gerencia y VP al registrar un caso' },
  { codigo: 'plazo_por_vencer', nombre: 'Plazo por vencer', nota: 'El plazo de notificación a la autoridad está por vencer' },
  { codigo: 'escalamiento_vencido', nombre: 'Escalamiento por plazo vencido', nota: 'El plazo venció sin documento enviado; se escala' },
  { codigo: 'informe_final_por_vencer', nombre: 'Informe final por vencer', nota: 'El plazo del informe final está por vencer' },
  { codigo: 'informe_final_escalamiento', nombre: 'Escalamiento del informe final', nota: 'El informe final venció sin remitirse; se escala' },
  { codigo: 'cargo_pendiente', nombre: 'Acuse/cargo pendiente', nota: 'La carta se presentó y la autoridad no devolvió el cargo' },
  { codigo: 'especialista_invitado', nombre: 'Especialista invitado', nota: 'Se invitó a un especialista a la evaluación' },
  { codigo: 'caso_cerrado', nombre: 'Caso cerrado' },
];

/**
 * Un código en `snake_case` escrito como se lee: `plazo_interno_vencido` → «Plazo interno
 * vencido». No traduce ni adivina nada — solo quita los guiones bajos y sube la primera
 * letra—, así que el código sigue siendo reconocible para quien lo busque en la base.
 */
const comoSeLee = (codigo: string): string => {
  const suelto = codigo.replaceAll('_', ' ').trim();
  return suelto ? suelto.charAt(0).toUpperCase() + suelto.slice(1) : codigo;
};

/**
 * La etiqueta de un código de aviso.
 *
 * La lista sigue ABIERTA por diseño (ver la cabecera): un código que el catálogo no
 * conoce NO se oculta. Lo que cambia desde el 22-sep-2026 es que tampoco se enseña en
 * crudo: QA encontró en la lista de destinatarios `plazo_interno_vencido`, `plazo_vencido`
 * y `escalamiento_gerencia` tal cual, con guiones bajos, junto a los que sí tienen
 * etiqueta. Mostrarlo legible conserva la información —el código se sigue reconociendo—
 * y deja de parecer un dato a medio pintar.
 */
export const nombreDelTipoDeAviso = (codigo: string | null | undefined): string =>
  TIPOS_DE_AVISO.find((t) => t.codigo === codigo)?.nombre
  ?? (codigo ? comoSeLee(codigo) : '—');

/** Las opciones del desplegable, en el orden del catálogo. */
export const opcionesDeTipoDeAviso = (): { value: string; label: string; hint?: string }[] =>
  TIPOS_DE_AVISO.map((t) => ({ value: t.codigo, label: t.nombre, ...(t.nota ? { hint: t.nota } : {}) }));
